import { Building2, Target, Users, TrendingUp, Landmark, Warehouse } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';

export const About = () => {
  const principles = [
    {
      icon: Target,
      title: 'Mission',
      description: 'To protect brands while scaling distribution through compliant, professional e-commerce operations that serve manufacturers, distributors, and end customers with integrity.'
    },
    {
      icon: Users,
      title: 'Partnership Approach',
      description: 'We operate as an extension of your brand, not as an arbitrage reseller. Long-term relationships built on transparency, compliance, and mutual growth.'
    },
    {
      icon: TrendingUp,
      title: 'Sustainable Growth',
      description: 'Strategic inventory planning, brand-approved marketing investment, and operational excellence that supports both your business objectives and market stability.'
    }
  ];

  return (
    <div className="min-h-screen">
      {/* Hero */}
      <section className="relative text-white overflow-hidden" style={{ background: 'linear-gradient(135deg, #0a192f 0%, #112240 50%, #0a192f 100%)' }}>
        <div className="absolute inset-0 opacity-[0.04]" style={{ backgroundImage: 'radial-gradient(circle at 25% 25%, rgba(127,211,197,0.3) 0%, transparent 50%), radial-gradient(circle at 75% 75%, rgba(127,211,197,0.15) 0%, transparent 50%)' }}></div>
        <div className="relative max-w-7xl mx-auto px-6 py-24 md:py-32">
          <div className="grid md:grid-cols-2 items-center" style={{ gap: '60px' }}>
            <div className="animate-fade-in" style={{ maxWidth: '560px' }}>
              <h1
                style={{
                  fontFamily: "'General Sans', sans-serif",
                  fontSize: 'clamp(36px, 4.5vw, 56px)',
                  lineHeight: 1.1,
                  fontWeight: 700,
                  letterSpacing: '-0.025em',
                  marginBottom: '24px',
                }}
              >
                Built to Scale Brands Across Marketplaces
              </h1>
              <p
                className="text-gray-300"
                style={{
                  fontFamily: "'Inter', sans-serif",
                  fontSize: '17px',
                  lineHeight: 1.7,
                  opacity: 0.9,
                  maxWidth: '480px',
                  marginBottom: '32px',
                }}
              >
                VERLINE partners with brands to drive controlled, scalable growth across Amazon, Walmart, and beyond — combining distribution, operational excellence, and marketplace expertise.
              </p>
              <Link to="/partner">
                <Button size="lg" className="text-white font-semibold shadow-lg hover:shadow-xl transition-all px-8 py-3 text-base group" style={{ borderRadius: '10px', backgroundColor: '#2A8A6E' }}
                  onMouseEnter={e => { e.currentTarget.style.backgroundColor = '#237a60'; e.currentTarget.style.transform = 'translateY(-1px)'; }}
                  onMouseLeave={e => { e.currentTarget.style.backgroundColor = '#2A8A6E'; e.currentTarget.style.transform = 'translateY(0)'; }}
                >
                  Scale With Us <span style={{ display: 'inline-block', marginLeft: '4px', transition: 'transform 0.25s ease' }} className="group-hover:translate-x-0.5">↗</span>
                </Button>
              </Link>
            </div>
            <div className="animate-fade-in hidden md:block" style={{ animationDelay: '0.2s', position: 'relative' }}>
              {/* Soft dark overlay */}
              <div style={{ position: 'absolute', inset: 0, background: 'rgba(10,25,47,0.2)', zIndex: 1, borderRadius: '10px' }}></div>
              <img
                src="/hero-analytics-about.png"
                alt="Marketplace growth and performance analytics"
                className="w-full object-cover"
                style={{ borderRadius: '10px', minHeight: '480px', boxShadow: '0 32px 64px rgba(0,0,0,0.35)', objectPosition: '15% center' }}
              />
            </div>
          </div>
        </div>
      </section>

      {/* Our Approach */}
      <section style={{ background: 'linear-gradient(180deg, #f8f9fb 0%, #f1f3f6 100%)', padding: '100px 0 108px' }}>
        <div style={{ maxWidth: '1240px', margin: '0 auto', padding: '0 24px' }}>
          <div className="grid md:grid-cols-2 items-center" style={{ gap: '100px' }}>
            {/* Left — text */}
            <div style={{ maxWidth: '560px' }}>
              <span className="font-semibold" style={{ fontSize: '12px', letterSpacing: '0.18em', display: 'block', marginBottom: '16px', color: '#2A8A6E' }}>OUR APPROACH</span>
              <h2
                className="font-bold text-navy-900"
                style={{
                  fontFamily: "'General Sans', sans-serif",
                  fontSize: 'clamp(38px, 4.5vw, 60px)',
                  lineHeight: 1.08,
                  letterSpacing: '-0.03em',
                  marginBottom: '24px',
                }}
              >
                Built for Controlled Marketplace Growth
              </h2>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '18px', marginBottom: '32px' }}>
                <p style={{ fontSize: '16px', lineHeight: 1.75, color: '#6b7280' }}>
                  We partner with brands to scale across Amazon, Walmart, and direct channels through disciplined execution and structured expansion.
                </p>
                <p style={{ fontSize: '16px', lineHeight: 1.75, color: '#6b7280' }}>
                  Our approach prioritizes pricing integrity, operational control, and long-term brand positioning — not short-term volume.
                </p>
                <p style={{ fontSize: '16px', lineHeight: 1.75, color: '#6b7280' }}>
                  Since 2020, we've worked alongside brands focused on sustainable growth, delivering consistency across distribution, fulfillment, and marketplace performance.
                </p>
              </div>

              <div style={{ borderLeft: '3px solid #2A8A6E', paddingLeft: '16px' }}>
                <p className="font-bold" style={{ fontSize: '17px', lineHeight: 1.5, color: '#0a192f' }}>
                  We build systems — not quick wins.
                </p>
              </div>
            </div>

            {/* Right — image */}
            <div style={{ position: 'relative' }}>
              <div style={{ position: 'absolute', inset: '-20px', background: 'radial-gradient(circle at center, rgba(127,211,197,0.08) 0%, transparent 70%)', borderRadius: '32px', zIndex: 0 }}></div>
              <div style={{ borderRadius: '20px', overflow: 'hidden', boxShadow: '0 32px 72px rgba(10,25,47,0.14), 0 8px 24px rgba(10,25,47,0.06)', position: 'relative', zIndex: 1 }}>
                <img
                  src="/steph-warehouse.jpg"
                  alt="Warehouse operations team member"
                  style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'center 25%', display: 'block', minHeight: '520px' }}
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Meet the Team */}
      <section className="py-28" style={{ background: '#0f2137' }} data-testid="team-section">
        <div style={{ maxWidth: '1320px', margin: '0 auto', padding: '0 24px' }}>
          <div className="text-center" style={{ marginBottom: '52px' }}>
            <span className="text-teal-400 font-semibold tracking-widest" style={{ fontSize: '12px', letterSpacing: '0.15em', display: 'block', marginBottom: '12px' }}>OUR TEAM</span>
            <h2 className="font-bold text-white" style={{ fontSize: '32px', lineHeight: 1.2 }}>Meet the Team</h2>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4" style={{ gap: '26px' }}>
            {[
              { img: '/cristian-veres.jpg', title: 'CEO & FOUNDER', name: 'Cristian', crop: 'center 20%', scale: 'none' },
              { img: '/team-andrew.png', title: 'MANAGER', name: 'Andrew', crop: 'center 20%', scale: 'none' },
              { img: '/team-carmen.png', title: 'INFLUENCER & MARKETING SPECIALIST', name: 'Carmen', crop: 'center 25%', scale: 'scale(1.35)' },
              { img: '/team-sandy.png', title: 'WAREHOUSE MANAGER', name: 'Sandy', crop: 'center 20%', scale: 'none' },
            ].map((member) => (
              <div
                key={member.name}
                data-testid={`team-card-${member.name.toLowerCase().replace(/\s+/g, '-')}`}
                style={{
                  backgroundColor: '#182d45',
                  borderRadius: '18px',
                  padding: '28px 20px 32px',
                  border: '1px solid rgba(255,255,255,0.06)',
                  boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
                  transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                  cursor: 'default',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                }}
                onMouseEnter={e => {
                  e.currentTarget.style.transform = 'translateY(-5px)';
                  e.currentTarget.style.boxShadow = '0 12px 36px rgba(0,0,0,0.25)';
                }}
                onMouseLeave={e => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 4px 20px rgba(0,0,0,0.15)';
                }}
              >
                <div style={{ width: '180px', height: '180px', borderRadius: '14px', overflow: 'hidden', marginBottom: '20px', flexShrink: 0 }}>
                  <img
                    src={member.img}
                    alt={member.name}
                    style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: member.crop, display: 'block', transform: member.scale }}
                  />
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flexGrow: 1, justifyContent: 'flex-end' }}>
                  <span style={{ fontSize: '11px', fontWeight: 600, color: '#7FD3C5', letterSpacing: '0.12em', display: 'block', marginBottom: '8px', textAlign: 'center', lineHeight: 1.4, minHeight: '30px', display: 'flex', alignItems: 'flex-end', justifyContent: 'center' }}>{member.title}</span>
                  <h3 className="font-bold text-white" style={{ fontSize: '20px', lineHeight: 1.3, textAlign: 'center' }}>{member.name}</h3>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Credibility Strip — Dark Navy */}
      <section className="py-16 bg-navy-950" data-testid="credibility-strip">
        <div className="max-w-7xl mx-auto px-6">
          <h2 className="text-2xl md:text-3xl font-bold text-white text-center mb-10">Trusted Marketplace Distribution</h2>
          <div className="flex flex-col md:flex-row items-center justify-center gap-12 md:gap-20">
            <div className="text-center">
              <div className="text-3xl md:text-4xl font-bold text-white mb-1">$40M+</div>
              <div className="text-sm text-teal-400 font-medium tracking-wide">Marketplace Revenue</div>
            </div>
            <div className="hidden md:block w-px h-14 bg-gray-700"></div>
            <div className="text-center">
              <div className="text-3xl md:text-4xl font-bold text-white mb-1">Multi-Channel</div>
              <div className="text-sm text-teal-400 font-medium tracking-wide">Operations</div>
            </div>
            <div className="hidden md:block w-px h-14 bg-gray-700"></div>
            <div className="text-center">
              <div className="text-3xl md:text-4xl font-bold text-white mb-1">U.S.-Based</div>
              <div className="text-sm text-teal-400 font-medium tracking-wide">Fulfillment</div>
            </div>
          </div>
        </div>
      </section>

      {/* Mission & Values — Light Gray */}
      <section className="py-24" style={{ backgroundColor: '#F7F9FB' }}>
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-14">
            <h2 className="text-3xl font-bold text-navy-900 mb-4">Our Principles</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Every decision we make is guided by these core commitments to our brand partners.
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {principles.map((principle, index) => {
              const Icon = principle.icon;
              return (
                <div key={index} className="text-center" style={{ backgroundColor: '#0f2137', borderRadius: '16px', padding: '36px 28px', border: '1px solid rgba(127,211,197,0.12)', boxShadow: '0 4px 24px rgba(10,25,47,0.2)' }}>
                  <div className="inline-flex items-center justify-center mb-4">
                    <Icon style={{ width: '40px', height: '40px', color: '#7FD3C5' }} strokeWidth={1.75} />
                  </div>
                  <h3 className="text-xl font-bold mb-3" style={{ color: 'rgba(255,255,255,0.95)' }}>{principle.title}</h3>
                  <p style={{ color: '#C9D3DF', lineHeight: 1.65, fontWeight: 450 }}>{principle.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

    </div>
  );
};
